import { generateWebRTCpayload } from './dataUtils.js'
import { resolveTransport } from './transport.js'

async function sleep(milliseconds) {
    return await new Promise((resolve) => setTimeout(resolve, milliseconds))
}

export function PeerBinaryFactory(options) {
    const { UnChunker, Peer, wrtc } = options // dependency injection

    return class PeerBinary extends Peer {
        constructor(options) {
            // Transport tuning (webrtc-transfer-tuning): water marks, timeouts,
            // the stream gate and the ICE policy — see transport.js. Resolved
            // before super() so the ICE policy can reach the PeerConnection.
            const transport = resolveTransport(options.transport)

            // Map the app's top-level `iceServers` / `iceTransportPolicy` into
            // simple-peer's `config` (the RTCPeerConnection configuration).
            // simple-peer reads ONLY `opts.config` — a top-level `iceServers` is
            // silently ignored, so without this remap the fetched TURN credentials
            // never reach the PeerConnection and it falls back to simple-peer's
            // default STUN-only config (no TURN → relay never available).
            // An explicit `options.config` wins if provided (e.g. a STUN-only
            // override to test the no-TURN / direct-only path).
            const config = { ...(options.config || {}) }
            if (options.iceServers && !config.iceServers) {
                config.iceServers = options.iceServers
            }
            const policy = options.iceTransportPolicy || transport.iceTransportPolicy
            if (policy && !config.iceTransportPolicy) {
                config.iceTransportPolicy = policy
            }
            super({ wrtc, ...options, config })
            this.transport = transport
            // An initiator's simple-peer constructor can create the channel
            // (and so run _setupData) before this.transport exists; apply the
            // low mark now for that order, in _setupData for the other.
            this._applyLowWater()
            this.PER_CHUNK_WAIT =
                options.PER_CHUNK_WAIT !== undefined
                    ? options.PER_CHUNK_WAIT
                    : 0
            this._transportStats = {
                payloads: 0, // sendBig calls
                payloadsFailed: 0, // returned false (closed channel, timeout, error)
                chunks: 0, // channel messages written (headers excluded)
                bytes: 0, // bytes handed to channel.send (headers excluded)
                drainWaits: 0, // times sendBig sat on bufferedamountlow
                drainMs: 0, // total time spent in those waits
                drainTimeouts: 0, // waits that hit transport.drainTimeout
                sendTimeouts: 0, // payloads that hit transport.sendTimeout
            }
            this._registerDataMessage()
            this.unchunker = new UnChunker()
            this.unchunker.onData = (val) => {
                this.emit('dataBig', val)
            }
            this.on('close', () => {
                if (this.unchunker?.destroy) this.unchunker.destroy()
            })

            this.peerId = options.peerId
        }

        //want to overide these 2 functions I think.
        _registerDataMessage() {
            this.on('data', (data) => {
                //when its done with a complete chunk, call this.emit('dataBig', completed)
                this.unchunker.registerChunk(data)
            })
        }

        // simple-peer creates (initiator) or receives (responder) the data
        // channel here and pins bufferedAmountLowThreshold to its own 64 KB
        // constant. Re-apply ours so the low-water mark is chosen, not
        // inherited.
        _setupData(event) {
            super._setupData(event)
            this._applyLowWater()
        }

        _applyLowWater() {
            const ch = this._channel
            if (!this.transport) return // _setupData ran inside super(); constructor re-applies
            if (ch && typeof ch.bufferedAmountLowThreshold === 'number') {
                ch.bufferedAmountLowThreshold = this.transport.lowWater
            }
        }

        /**
         * Change transport tuning on a live peer. Validated like the
         * constructor option; the low mark is re-applied to the open channel
         * immediately, the rest take effect on the next sendBig.
         * @param {object} patch - any subset of transport.js keys
         * @returns {object} the resolved transport
         */
        setTransport(patch) {
            this.transport = resolveTransport(this.transport, patch)
            this._applyLowWater()
            return this.transport
        }

        /** Counters plus the channel's live buffer state — the harness reads this. */
        transportStats() {
            const ch = this._channel
            return {
                ...this._transportStats,
                bufferedAmount: ch ? ch.bufferedAmount : null,
                bufferedAmountLowThreshold: ch ? ch.bufferedAmountLowThreshold : null,
                readyState: ch ? ch.readyState : null,
                highWater: this.transport.highWater,
                lowWater: this.transport.lowWater,
                drainTimeout: this.transport.drainTimeout,
                sendTimeout: this.transport.sendTimeout,
            }
        }

        /**
         * Wait for the channel's bufferedamountlow event, bounded by
         * transport.drainTimeout and by the payload deadline, whichever is
         * sooner. Resolves true when drained, false on timeout (the listener
         * is removed either way — a timed-out wait used to leak it).
         */
        _waitForDrain(deadline) {
            const ch = this._channel
            const stats = this._transportStats
            const { drainTimeout } = this.transport
            const untilDeadline = deadline - Date.now()
            const drainBudget = drainTimeout > 0 ? drainTimeout : Infinity
            const boundedByDrain = drainBudget <= untilDeadline
            const budget = Math.min(drainBudget, untilDeadline)
            if (!(budget > 0)) {
                stats.sendTimeouts++
                console.warn(`sendBig: send timeout reached before backpressure wait (bufferedAmount=${ch.bufferedAmount})`)
                return Promise.resolve(false)
            }
            stats.drainWaits++
            const started = Date.now()
            return new Promise((resolve) => {
                let timer = null
                const onLow = () => {
                    if (timer) clearTimeout(timer)
                    stats.drainMs += Date.now() - started
                    resolve(true)
                }
                if (budget !== Infinity) {
                    timer = setTimeout(() => {
                        ch.removeEventListener('bufferedamountlow', onLow)
                        const waited = Date.now() - started
                        stats.drainMs += waited
                        if (boundedByDrain) stats.drainTimeouts++
                        else stats.sendTimeouts++
                        console.warn(
                            `sendBig: backpressure ${boundedByDrain ? 'drain' : 'send'} timeout after ${waited}ms ` +
                                `(bufferedAmount=${ch.bufferedAmount}, lowWater=${this.transport.lowWater})`
                        )
                        resolve(false)
                    }, budget)
                }
                ch.addEventListener('bufferedamountlow', onLow, { once: true })
            })
        }

        /**
         * Send one msgpack payload as chunked channel messages with
         * backpressure. Resolves true when every chunk was handed to the
         * channel, false when the channel was not open, a drain or send
         * timeout fired, or sending threw. A false return means the remote
         * side will not receive the payload — stream senders must abort.
         */
        async sendBig(data) {
            const stats = this._transportStats
            stats.payloads++
            try {
                if (!this._channel || this._channel.readyState !== 'open') {
                    console.warn('sendBig: data channel not ready, skipping')
                    stats.payloadsFailed++
                    return false
                }
                const stuff = await generateWebRTCpayload(data)
                const { highWater, sendTimeout } = this.transport
                const startTime = Date.now()
                const deadline = sendTimeout > 0 ? startTime + sendTimeout : Infinity
                await this.send(stuff.header)
                for (let i = 0; i < stuff.chunks.length; i++) {
                    if (!this._channel || this._channel.readyState !== 'open') {
                        console.warn('sendBig: channel closed mid-send, aborting')
                        stats.payloadsFailed++
                        return false
                    }
                    // Backpressure: above the high mark, wait for the channel
                    // to drain to the low mark (transport.lowWater).
                    if (this._channel.bufferedAmount > highWater) {
                        const drained = await this._waitForDrain(deadline)
                        if (!drained) {
                            stats.payloadsFailed++
                            return false
                        }
                    }
                    if (Date.now() > deadline) {
                        console.warn(`sendBig: send timeout after ${i}/${stuff.chunks.length} chunks`)
                        stats.sendTimeouts++
                        stats.payloadsFailed++
                        return false
                    }
                    const chunk = stuff.chunks[i]
                    await this.send(chunk)
                    stats.chunks++
                    stats.bytes += chunk.byteLength ?? chunk.length ?? 0
                    if (this.PER_CHUNK_WAIT > 0) await sleep(this.PER_CHUNK_WAIT)
                }
                return true
            } catch (error) {
                console.error('sendBig error: ', error)
                stats.payloadsFailed++
                return false
            }
        }
    }
}
